import {
  Button,
  Dialog,
  Flex,
  Heading,
  List,
  Modal,
  Select,
  Spacer,
  Tabs,
  Text,
  Toggle,
} from '@oliasoft-open-source/react-ui-library';
import translations from 'client/internationalization/translation-map.json';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import JSONPretty from 'react-json-pretty';
import JSONPrettyMon from 'react-json-pretty/dist/monikai';
import { useDispatch, useSelector } from 'react-redux';
import { IObjectAssociatedVersion, IObjectVersion } from 'common/interfaces/version';
import { VersionOrigin } from 'common/enums/version-enums';
import { IUser } from 'common/interfaces/customer-interfaces';
import { IObject } from 'common/interfaces/hierarchy-entities';
import { EntityType } from 'common/enums/entity-enums';
import { TableViewer } from 'client/components/data-viewer/table-viewer/table-viewer';
import { hasAssociatedData } from 'common/utils/is-multiple-tables';
import { DateService } from 'common/services/date-service';
import { RootState } from 'client/store/configureStore';
import { getCompanyUsers } from 'client/store/entities/user/selectors';
import {
  getObjectVersions,
  getObjectAssociatedVersions,
  loadMoreObjectVersions,
  loadMoreObjectAssociatedVersions,
  restoreObjectVersion,
  restoreObjectAssociatedVersion,
  deleteObjectVersion,
  deleteObjectAssociatedVersion,
  getObjectVersion,
  getObjectAssociatedVersion,
} from 'client/store/entities/version/version';
import {
  getVersionsForObject,
  getVersionsHasMore,
  getAssociatedVersionsForObject,
  getAssociatedVersionsHasMore,
  getVersionsIsLoading,
  getVersionsIsRestoring,
  getVersionData,
  getAssociatedVersionData,
} from 'client/store/entities/version/selectors';

const DataTabType = Object.freeze({
  Main: 'main',
  Associated: 'associated',
});

const ContentTabType = Object.freeze({
  Table: 'table',
  Json: 'json',
  Properties: 'properties',
  File: 'file',
});

type RestoreModalProps = {
  version: IObjectVersion | IObjectAssociatedVersion;
  isRestoring: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const RestoreConfirmModal = ({
  version,
  isRestoring,
  onClose,
  onConfirm,
}: RestoreModalProps) => {
  const { t } = useTranslation();
  return (
    <Modal centered visible onEscape={onClose}>
      <Dialog
        dialog={{
          heading: t(translations.version_restoreConfirmTitle),
          content: t(translations.version_restoreConfirmContent, { version: version.version }),
          footer: (
            <>
              <Button
                label={t(translations.version_restore)}
                colored="green"
                disabled={isRestoring}
                onClick={onConfirm}
              />
              <Button label={t(translations.cancel)} onClick={onClose} />
            </>
          ),
          onClose,
        }}
      />
    </Modal>
  );
};

type DeleteModalProps = {
  version: IObjectVersion | IObjectAssociatedVersion;
  onClose: () => void;
  onConfirm: () => void;
};

const DeleteConfirmModal = ({ version, onClose, onConfirm }: DeleteModalProps) => {
  const { t } = useTranslation();
  return (
    <Modal centered visible onEscape={onClose}>
      <Dialog
        dialog={{
          heading: t(translations.version_deleteConfirmTitle),
          content: t(translations.version_deleteConfirmContent, { version: version.version }),
          footer: (
            <>
              <Button label={t(translations.version_delete)} colored="red" onClick={onConfirm} />
              <Button label={t(translations.cancel)} onClick={onClose} />
            </>
          ),
          onClose,
        }}
      />
    </Modal>
  );
};

type VersionDataPreviewProps = {
  objectid: string;
  dataObject: IObject;
  version: IObjectVersion | IObjectAssociatedVersion;
  versions: IObjectVersion[];
  hasMore: boolean;
  isAssociated: boolean;
  label: string;
  onClose: () => void;
  onLoadMore: () => void;
};

type ContentTab = typeof ContentTabType[keyof typeof ContentTabType];

const VersionDataPreview = ({
  objectid, dataObject, version, versions, hasMore, isAssociated, label, onClose, onLoadMore,
}: VersionDataPreviewProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isLoading = useSelector(getVersionsIsLoading);
  const versionData = useSelector((state: RootState) => isAssociated
    ? getAssociatedVersionData(state, version.versionid)
    : getVersionData(state, version.versionid));

  const [activeContentTab, setActiveContentTab] = useState<ContentTab>(isAssociated ? ContentTabType.Json : ContentTabType.Table);
  const [compareEnabled, setCompareEnabled] = useState(false);
  const contentTabOptions = isAssociated
    ? [{ label: 'JSON', value: ContentTabType.Json }]
    : [{ label: t(translations.viewer_table), value: ContentTabType.Table }];

  const compareVersionOptions = versions
    .filter((v) => version.versionid !== v.versionid)
    .map((v) => (v.version === dataObject?.version
      ? { label: `v${v.version} (${t(translations.version_latest)})`, value: v.versionid }
      : { label: `v${v.version}`, value: v.versionid }
    ));

  const [compareVersionId, setCompareVersionId] = useState<string>(compareVersionOptions?.[0]?.value);

  const compareVersionData = useSelector((state: RootState) => {
    if (!compareEnabled || !compareVersionId) return;
    return isAssociated ? getAssociatedVersionData(state, compareVersionId) : getVersionData(state, compareVersionId);
  }) as { [k: string]: any } | undefined;

  useEffect(() => {
    if (isAssociated) {
      dispatch(getObjectAssociatedVersion(objectid, version.versionid) as any);
    } else {
      dispatch(getObjectVersion(objectid, version.versionid) as any);
    }
  }, [version.versionid]);

  useEffect(() => {
    if (!compareEnabled || !compareVersionId) return;
    if (isAssociated) {
      dispatch(getObjectAssociatedVersion(objectid, compareVersionId) as any);
    } else {
      dispatch(getObjectVersion(objectid, compareVersionId) as any);
    }
  }, [compareEnabled, compareVersionId]);

  const renderContent = () => {
    if (isLoading || versionData === undefined) {
      return <Text muted>{t(translations.hierarchy_loading)}</Text>;
    }
    if (versionData == null) {
      return <Text muted>{t(translations.viewer_noData)}</Text>;
    }
    if (isAssociated) {
      return <JSONPretty data={versionData} theme={JSONPrettyMon} />;
    }
    return (
      <TableViewer
        key={version.versionid}
        dataObject={{ ...dataObject, data: versionData } as IObject}
        setSelectedData={() => {}}
        withPaste={false}
        dataToCompare={compareVersionData}
      />
    );
  };

  return (
    <div>
      <Flex gap>
        <Heading>{label}</Heading>
          <Button
            small
            icon="chevron left"
            label={t(translations.version_backToHistory)}
            onClick={onClose}
          />
      </Flex>
      <Spacer />
      <Flex gap alignItems="center">
        {!isAssociated && compareVersionId && (
          <>
            <Toggle
              label={t(translations.version_compare)}
              checked={compareEnabled}
              onChange={(evt) => setCompareEnabled(evt.target.checked)}
            />
            {compareEnabled && (
              <Select
                width={150}
                options={compareVersionOptions}
                value={compareVersionId}
                onChange={(evt) => setCompareVersionId(evt.target.value)}
              />
            )}
            {compareEnabled && hasMore && (
              <Button
                small
                label={isLoading ? t(translations.hierarchy_loading) : t(translations.version_loadMore)}
                disabled={isLoading}
                onClick={onLoadMore}
              />
            )}
          </>
        )}
      </Flex>
      <Tabs
        options={contentTabOptions}
        value={contentTabOptions.find((option) => option.value === activeContentTab) || contentTabOptions[0]}
        onChange={(evt) => setActiveContentTab(evt.target.value)}
        padding
      />
      <div>
        {renderContent()}
      </div>
    </div>
  );
};

type VersionListProps = {
  versions: (IObjectVersion | IObjectAssociatedVersion)[];
  hasMore: boolean;
  isLoading: boolean;
  isRestoring: boolean;
  users: IUser[];
  previewVersionId: string | null;
  onLoadMore: () => void;
  onRestore: (v: IObjectVersion | IObjectAssociatedVersion) => void;
  onDelete: (v: IObjectVersion | IObjectAssociatedVersion) => void;
  onView: (v: IObjectVersion | IObjectAssociatedVersion) => void;
};

const VersionList = ({
  versions, hasMore, isLoading, isRestoring, users, previewVersionId,
  onLoadMore, onRestore, onDelete, onView,
}: VersionListProps) => {
  const { t } = useTranslation();

  const resolveUserName = (subjectId: string | null) => {
    if (!subjectId) return null;
    const user = users.find((u) => u.subjectId === subjectId);
    return user ? `${user.firstName} ${user.lastName}`.trim() : subjectId;
  };

  if ((isLoading || isRestoring) && versions.length === 0) {
    return <Text>{t(translations.hierarchy_loading)}</Text>;
  }

  if (!isLoading && versions.length === 0) {
    return <Text muted>{t(translations.version_noVersions)}</Text>;
  }

  const items = versions.map((v, index) => {
    const isLatest = index === 0;
    return {
      id: v.versionid,
      active: previewVersionId === v.versionid,
      onClick: () => onView(v),
      name: <Text bold>{isLatest ? `v${v.version} (${t(translations.version_latest)})` : `v${v.version}`}</Text>,
      details: (
        <Flex direction="column" gap={2}>
          <Text small>{DateService.format(v.changed_at)}</Text>
          {v.changed_by && (
            <Text small muted>{t(translations.version_changedBy)}: {resolveUserName(v.changed_by)}</Text>
          )}
          {v.origin && v.origin !== VersionOrigin.User && (
            <Text small muted>{t(translations.version_origin)}: {v.origin}{v.integration_name ? ` (${v.integration_name})` : ''}</Text>
          )}
          {v.change_note && (
            <Text small muted>{v.change_note}</Text>
          )}
        </Flex>
      ),
      actions: [
        ...(isLatest ? [] : [{
          icon: 'rotate',
          tooltip: t(translations.version_restore),
          onClick: () => onRestore(v),
        }]),
        {
          icon: 'delete',
          tooltip: t(translations.version_delete),
          onClick: () => onDelete(v),
        },
      ],
    };
  });

  return (
    <>
      <List noHeader list={{ items }} />

      {hasMore && (
        <>
          <Spacer />
          <Flex justifyContent="center">
            <Button
              label={isLoading ? t(translations.hierarchy_loading) : t(translations.version_loadMore)}
              disabled={isLoading}
              onClick={onLoadMore}
            />
          </Flex>
        </>
      )}
    </>
  );
};

type VersionHistoryProps = {
  objectid: string;
  nodeid: string;
  entityType?: string;
  dataObject: IObject;
};

export const VersionHistory = ({
  objectid, nodeid, entityType, dataObject,
}: VersionHistoryProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [activeDataTab, setActiveDataTab] = useState<typeof DataTabType[keyof typeof DataTabType]>(DataTabType.Main);
  const versions = useSelector((state: RootState) => getVersionsForObject(state, objectid));
  const hasMore = useSelector((state: RootState) => getVersionsHasMore(state, objectid));
  const associatedVersions = useSelector((state: RootState) => getAssociatedVersionsForObject(state, objectid));
  const associatedHasMore = useSelector((state: RootState) => getAssociatedVersionsHasMore(state, objectid));
  const isLoading = useSelector(getVersionsIsLoading);
  const isRestoring = useSelector(getVersionsIsRestoring);
  const users = useSelector(getCompanyUsers);

  const showAssociated = hasAssociatedData(entityType as EntityType);
  const isMainTabActive = activeDataTab === DataTabType.Main;
  const isAssociatedTabActive = showAssociated && activeDataTab === DataTabType.Associated;

  type VersionAction = { v: IObjectVersion | IObjectAssociatedVersion; isAssociated: boolean };
  const [selectedRestore, setSelectedRestore] = useState<VersionAction | null>(null);
  const [selectedDelete, setSelectedDelete] = useState<VersionAction | null>(null);

  type VersionPreview = { v: IObjectVersion | IObjectAssociatedVersion; isAssociated: boolean; label: string };
  const [selectedPreview, setSelectedPreview] = useState<VersionPreview | null>(null);

  const dataTabOptions = [
    { label: t(translations.version_mainData), value: DataTabType.Main },
    ...(showAssociated ? [{ label: t(translations.version_associatedData), value: DataTabType.Associated }] : []),
  ];

  useEffect(() => {
    setSelectedPreview(null)
    dispatch(getObjectVersions(objectid) as any);
    if (showAssociated) {
      dispatch(getObjectAssociatedVersions(objectid) as any);
    }
  }, [objectid]);

  const handleRestoreConfirm = () => {
    if (!selectedRestore) return;
    if (selectedRestore.isAssociated) {
      dispatch(restoreObjectAssociatedVersion(objectid, selectedRestore.v.versionid, nodeid) as any);
    } else {
      dispatch(restoreObjectVersion(objectid, selectedRestore.v.versionid, nodeid) as any);
    }
    setSelectedRestore(null);
  };

  const handleDeleteConfirm = () => {
    if (!selectedDelete) return;
    if (selectedDelete.isAssociated) {
      dispatch(deleteObjectAssociatedVersion(objectid, selectedDelete.v.versionid) as any);
    } else {
      dispatch(deleteObjectVersion(objectid, selectedDelete.v.versionid) as any);
    }
    setSelectedDelete(null);
  };

  const handleLoadMore = useCallback(() => {
    dispatch(loadMoreObjectVersions(objectid, versions.length) as any);
  }, [dispatch, objectid, versions.length]);

  const handleLoadMoreAssociated = useCallback(() => {
    dispatch(loadMoreObjectAssociatedVersions(objectid, associatedVersions.length) as any);
  }, [dispatch, objectid, associatedVersions.length]);

  const handleView = (v: IObjectVersion | IObjectAssociatedVersion, isAssociated: boolean) => {
    if (selectedPreview?.v.versionid === v.versionid) {
      setSelectedPreview(null);
      return;
    }
    const sectionLabel = isAssociated ? t(translations.version_associatedData) : t(translations.version_mainData);
    setSelectedPreview({ v, isAssociated, label: `v${v.version} \u2013 ${sectionLabel}` });
  };

  const commonListProps = {
    isLoading,
    isRestoring,
    users,
    previewVersionId: selectedPreview?.v.versionid ?? null,
  };

  return (
    <>
      {selectedPreview ? (
        <VersionDataPreview
          objectid={objectid}
          dataObject={dataObject}
          version={selectedPreview.v}
          versions={versions}
          hasMore={hasMore}
          isAssociated={selectedPreview.isAssociated}
          label={selectedPreview.label}
          onClose={() => setSelectedPreview(null)}
          onLoadMore={handleLoadMore}
        />
      ) : (
        <>
          <Heading>{t(translations.version_history)}</Heading>
          <Tabs
            options={dataTabOptions}
            value={dataTabOptions.find((option) => option.value === activeDataTab) || dataTabOptions[0]}
            onChange={(evt) => setActiveDataTab(evt.target.value)}
            padding
          />

          {isMainTabActive && (
            <VersionList
              {...commonListProps}
              versions={versions}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              onRestore={(v) => setSelectedRestore({ v, isAssociated: false })}
              onDelete={(v) => setSelectedDelete({ v, isAssociated: false })}
              onView={(v) => handleView(v, false)}
            />
          )}

          {isAssociatedTabActive && (
            <VersionList
              {...commonListProps}
              versions={associatedVersions}
              hasMore={associatedHasMore}
              onLoadMore={handleLoadMoreAssociated}
              onRestore={(v) => setSelectedRestore({ v, isAssociated: true })}
              onDelete={(v) => setSelectedDelete({ v, isAssociated: true })}
              onView={(v) => handleView(v, true)}
            />
          )}
        </>
      )}

      {selectedRestore && (
        <RestoreConfirmModal
          version={selectedRestore.v}
          isRestoring={isRestoring}
          onClose={() => setSelectedRestore(null)}
          onConfirm={handleRestoreConfirm}
        />
      )}

      {selectedDelete && (
        <DeleteConfirmModal
          version={selectedDelete.v}
          onClose={() => setSelectedDelete(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
};
